import { Stack } from '@cmg/design-system';
import { Form, FormikProvider, useFormik } from 'formik';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import { useConvertibleFilingTalkRanges } from '../../../../../../../../common/hooks/useConvertibleFilingTalkRanges';
import { InstitutionalIndicationOrderType } from '../../../../../../../../graphql';
import { useIndicationActivityBuySideContext } from '../../../../context/IndicationActivityBuySideContext';
import { IndicationSectionWithEditButtons } from '../../common/indication-section-edit-view/IndicationSectionWithEditButtons';
import IndicationSectionForm from '../../common/indication-section-form/IndicationSectionForm';
import { getInitialTouchedFields } from '../../common/indication-section-form/IndicationSectionForm.model';
import { PassIndicationDialog } from '../../common/PassIndicationDialog';
import {
  IndicationFormValues,
  validateIndicationSection,
} from '../../common/utils/IndicationSectionValidation';
import { usePassOnOffering } from '../common/hooks/usePassOnOffering';
import { createInitialValues as createInitialValuesPass } from '../indication-section-create-view/IndicationSectionCreateView.model';
import { useUpdateMyIndicationMutation } from './hooks/useUpdateMyIndicationMutation';
import { createInitialValues } from './IndicationSectionEditView.model';

export type Props = Readonly<{
  onFinishEditing: () => void;
  hasAllocationRanking: boolean;
}>;

const EMPTY_ARRAY = [];
const IndicationSectionEditView: React.FC<Props> = ({ hasAllocationRanking, onFinishEditing }) => {
  const { basicPublishedOffering, offeringId, orderBookConfiguration, publishedOffering } =
    useIndicationActivityBuySideContext();
  const offering = basicPublishedOffering!;
  const indication = offering.myInstitutionalIndication!;

  const [updateMyIndication] = useUpdateMyIndicationMutation(offeringId, onFinishEditing);

  const [isPassDialogOpen, setIsPassDialogOpen] = React.useState(false);

  const convertibleFilingTalkRanges = useConvertibleFilingTalkRanges(
    defaultTo(publishedOffering?.terms.filings, null)
  );

  const initialValues =
    indication.type === InstitutionalIndicationOrderType.Pass
      ? createInitialValuesPass(offering)
      : createInitialValues({
          indication,
          convertibleFilingTalkRanges,
          offeringType: offering.type,
          isIndicationPriceAsPercentageParEnabled:
            orderBookConfiguration.isIndicationPriceAsPercentageParEnabled,
        });

  const formik = useFormik<IndicationFormValues>({
    initialValues,
    validate: async values =>
      validateIndicationSection({
        values,
        isLimitIndicationsOnlyEnabled: orderBookConfiguration.isLimitIndicationsOnlyEnabled,
        isInvestorContactRequired: false,
        offeringType: offering.type,
        displayMissingContacts: false,
        isIndicationPriceAsPercentageParEnabled:
          orderBookConfiguration.isIndicationPriceAsPercentageParEnabled,
        isOnlySharesUnitOnIndicationsEnabled:
          orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled,
      }),
    validateOnMount: true,
    onSubmit: updateMyIndication,
    initialTouched: getInitialTouchedFields({
      interestLevels: initialValues.interestLevels,
      offeringType: offering.type,
      isIndicationPriceAsPercentageParEnabled:
        orderBookConfiguration.isIndicationPriceAsPercentageParEnabled,
    }),
  });
  const { isSubmitting, handleSubmit } = formik;

  const isPassButtonVisible = indication.type !== InstitutionalIndicationOrderType.Pass;

  const [onPass, isPassing] = usePassOnOffering({
    offeringId,
    indicationStatus: indication.status,
    onClose: () => setIsPassDialogOpen(false),
    onFinishEditing,
  });

  return (
    <IndicationSectionWithEditButtons
      indicationStatus={indication.status}
      isPassButtonVisible={isPassButtonVisible}
      isPassButtonDisabled={hasAllocationRanking || isSubmitting}
      handlePass={() => setIsPassDialogOpen(true)}
      handleDiscard={onFinishEditing}
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit}
    >
      <Stack gap={1}>
        <FormikProvider value={formik}>
          <Form>
            <IndicationSectionForm
              filings={defaultTo(publishedOffering?.terms.filings, null)}
              securityType={defaultTo(publishedOffering?.security.type, null)}
              tranches={defaultTo(offering.orderEntryProfile?.tranches, EMPTY_ARRAY)}
              instruments={defaultTo(publishedOffering?.instruments, null)}
              issuerName={defaultTo(publishedOffering?.issuer.name, null)}
              offeringType={offering.type}
              canEnterTranche={indication.type === InstitutionalIndicationOrderType.Pass}
              pricingCurrencyCode={offering.pricingCurrencyCode}
              securityTypeDisplayName={offering.securityTypeDisplayName!}
              isLimitIndicationsOnlyEnabled={orderBookConfiguration.isLimitIndicationsOnlyEnabled}
              isOnlySharesUnitOnIndicationsEnabled={
                orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled
              }
              isIndicationPriceAsPercentageParEnabled={
                orderBookConfiguration.isIndicationPriceAsPercentageParEnabled
              }
            />
          </Form>
        </FormikProvider>

        <PassIndicationDialog
          isOpen={isPassDialogOpen}
          isLoading={isPassing}
          onClose={() => setIsPassDialogOpen(false)}
          onPass={onPass}
        />
      </Stack>
    </IndicationSectionWithEditButtons>
  );
};

export default IndicationSectionEditView;
